// Navigation settings panel (Settings → «Меню»): the sidebar layout editor.
// Groups and their items are listed with ↑/↓ reordering and a visibility
// switch per item; a reset returns the default order. Items the current
// role/flags hide from the sidebar are not configurable here at all —
// the panel edits only what the user can actually see.

import { useMemo, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Button, Card, Switch, Tooltip } from 'antd';
import { ArrowDown, ArrowUp, Eye, EyeOff, RotateCcw } from 'lucide-react';

import { NAV_GROUPS } from '../../../components/layout/nav-items.js';
import { useFeatureFlag } from '@govard/feature-flags/react';
import { CRM_FLAG_IDS } from '../../../feature-flags/index.js';
import { useCurrentScope, useIsPlatformAdmin } from '../../../shared/scope/useCurrentScope.js';
import { useNavigationPreferencesStore } from '../navigationPreferencesStore.js';

/** One item row: icon + label + reorder buttons + visibility switch. */
function ItemRow({
  groupKey,
  itemRoute,
  label,
  icon,
  badge,
  hidden,
  isFirst,
  isLast,
}: {
  groupKey: string;
  itemRoute: string;
  label: string;
  icon: ReactElement;
  badge?: string | undefined;
  hidden: boolean;
  isFirst: boolean;
  isLast: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const moveItem = useNavigationPreferencesStore((state) => state.moveItem);
  const toggleItemHidden = useNavigationPreferencesStore((state) => state.toggleItemHidden);

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5"
      style={{ opacity: hidden ? 0.55 : 1 }}
      data-testid={`nav-settings-item-${groupKey}-${itemRoute}`}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center"
          style={{
            borderRadius: 'var(--ant-border-radius-sm, 6px)',
            border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
            color: hidden
              ? 'var(--ant-color-text-tertiary, #6d6963)'
              : 'var(--ant-color-primary-text, #b45309)',
          }}
        >
          {icon}
        </span>
        <span
          className="truncate text-[12.5px]"
          style={{
            color: hidden
              ? 'var(--ant-color-text-tertiary, #6d6963)'
              : 'var(--ant-color-text, #292524)',
            textDecoration: hidden ? 'line-through' : 'none',
          }}
        >
          {label}
        </span>
        {badge ? (
          <span
            className="rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide"
            style={{
              backgroundColor: 'var(--ant-color-primary-bg, #fffbeb)',
              color: 'var(--ant-color-primary-text, #b45309)',
              border: '1px solid var(--ant-color-primary-border, #fbbf24)',
            }}
          >
            {badge}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-1">
        <Button
          type="text"
          size="small"
          disabled={isFirst}
          icon={<ArrowUp size={14} />}
          aria-label={t('crm.settings.navigation.move_item_up', { label })}
          onClick={() => moveItem(groupKey, itemRoute, -1)}
        />
        <Button
          type="text"
          size="small"
          disabled={isLast}
          icon={<ArrowDown size={14} />}
          aria-label={t('crm.settings.navigation.move_item_down', { label })}
          onClick={() => moveItem(groupKey, itemRoute, 1)}
        />
        <Tooltip title={t('crm.settings.navigation.visibility_hint')}>
          <Switch
            size="small"
            checked={!hidden}
            onChange={() => toggleItemHidden(itemRoute)}
            aria-label={t('crm.settings.navigation.visibility', { label })}
          />
        </Tooltip>
      </span>
    </div>
  );
}

/** One group block: header row (reorder + label) + its items. */
function GroupBlock({
  groupKey,
  label,
  icon,
  items,
  isFirst,
  isLast,
}: {
  groupKey: string;
  label: string;
  icon: ReactElement;
  items: {
    to: string;
    label: string;
    icon: ReactElement;
    badge?: string | undefined;
  }[];
  isFirst: boolean;
  isLast: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const moveGroup = useNavigationPreferencesStore((state) => state.moveGroup);
  const hiddenItems = useNavigationPreferencesStore((state) => state.preferences.hiddenItems);
  const itemOrder = useNavigationPreferencesStore((state) => state.preferences.itemOrder);

  const hidden = new Set(hiddenItems);
  const preferred = itemOrder[groupKey];
  const byRoute = new Map(items.map((item) => [item.to, item]));
  const orderedRoutes: string[] = [];
  for (const route of preferred ?? []) {
    if (byRoute.has(route)) orderedRoutes.push(route);
  }
  for (const item of items) {
    if (!orderedRoutes.includes(item.to)) orderedRoutes.push(item.to);
  }

  return (
    <div
      style={{
        border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
        borderRadius: 'var(--ant-border-radius, 10px)',
        background: 'var(--ant-color-bg-container, #fffdfa)',
      }}
      data-testid={`nav-settings-group-${groupKey}`}
    >
      <div
        className="flex items-center gap-2 border-b px-3 py-2"
        style={{ borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)' }}
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center"
          style={{
            borderRadius: 'var(--ant-border-radius-sm, 6px)',
            background: 'var(--ant-color-primary-bg, #fffbeb)',
            border: '1px solid var(--ant-color-primary-border, #fbbf24)',
            color: 'var(--ant-color-primary-text, #b45309)',
          }}
        >
          {icon}
        </span>
        <span
          className="min-w-0 flex-1 truncate text-[12px] font-semibold uppercase tracking-[0.05em]"
          style={{ color: 'var(--ant-color-text-secondary, #57534e)' }}
        >
          {label}
        </span>
        <Button
          type="text"
          size="small"
          disabled={isFirst}
          icon={<ArrowUp size={14} />}
          aria-label={t('crm.settings.navigation.move_group_up', { label })}
          onClick={() => moveGroup(groupKey, -1)}
        />
        <Button
          type="text"
          size="small"
          disabled={isLast}
          icon={<ArrowDown size={14} />}
          aria-label={t('crm.settings.navigation.move_group_down', { label })}
          onClick={() => moveGroup(groupKey, 1)}
        />
      </div>
      <div className="py-1">
        {orderedRoutes.map((route, index) => {
          const item = byRoute.get(route)!;
          return (
            <ItemRow
              key={route}
              groupKey={groupKey}
              itemRoute={route}
              label={item.label}
              icon={item.icon}
              badge={item.badge}
              hidden={hidden.has(route)}
              isFirst={index === 0}
              isLast={index === orderedRoutes.length - 1}
            />
          );
        })}
      </div>
    </div>
  );
}

export function NavigationSettingsPanel(): ReactElement {
  const { t } = useTranslation();
  const resetPreferences = useNavigationPreferencesStore((state) => state.resetPreferences);
  const groupOrder = useNavigationPreferencesStore((state) => state.preferences.groupOrder);
  const hiddenItems = useNavigationPreferencesStore((state) => state.preferences.hiddenItems);

  const proMode = useFeatureFlag(CRM_FLAG_IDS.PRO_MODE);
  const runCenterEnabled = useFeatureFlag(CRM_FLAG_IDS.RUN_CENTER);
  const scope = useCurrentScope();
  const isPlatformAdmin = useIsPlatformAdmin();
  const canSeeOwnerAdminItems = scope !== null && ['owner', 'admin'].includes(scope.role);

  // Only what the sidebar can show is configurable; role/flag-gated entries
  // stay out of the editor entirely.
  const configurableGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            (item.pro !== true || proMode) &&
            (item.runCenter !== true || runCenterEnabled) &&
            (item.ownerAdminOnly !== true || canSeeOwnerAdminItems) &&
            (item.platformAdminOnly !== true || isPlatformAdmin),
        ),
      })).filter((group) => group.items.length > 0),
    [proMode, runCenterEnabled, canSeeOwnerAdminItems, isPlatformAdmin],
  );

  const orderedGroupKeys: string[] = [];
  for (const key of groupOrder) {
    if (configurableGroups.some((group) => group.key === key)) orderedGroupKeys.push(key);
  }
  for (const group of configurableGroups) {
    if (!orderedGroupKeys.includes(group.key)) orderedGroupKeys.push(group.key);
  }

  const hidden = new Set(hiddenItems);
  const visibleCount = configurableGroups.reduce(
    (count, group) => count + group.items.filter((item) => !hidden.has(item.to)).length,
    0,
  );

  return (
    <Card variant="borderless" data-testid="nav-settings-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[13px]" style={{ color: 'var(--ant-color-text-secondary)' }}>
          {t('crm.settings.navigation.subtitle', { count: visibleCount })}
        </p>
        <Button
          icon={<RotateCcw size={14} />}
          onClick={resetPreferences}
          data-testid="nav-settings-reset"
        >
          {t('crm.settings.navigation.reset')}
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        {orderedGroupKeys.map((key, index) => {
          const group = configurableGroups.find((candidate) => candidate.key === key)!;
          return (
            <GroupBlock
              key={key}
              groupKey={group.key}
              label={t(group.labelKey)}
              icon={<group.icon size={14} />}
              isFirst={index === 0}
              isLast={index === orderedGroupKeys.length - 1}
              items={group.items.map((item) => ({
                to: item.to,
                label: t(item.labelKey),
                icon: <item.icon size={12} />,
                badge: item.badge,
              }))}
            />
          );
        })}
      </div>
      <p
        className="mt-3 flex items-center gap-1.5 text-[11.5px]"
        style={{ color: 'var(--ant-color-text-tertiary)' }}
      >
        <Eye size={12} />
        {t('crm.settings.navigation.hidden_hint')}
        <EyeOff size={12} style={{ marginLeft: 2 }} />
      </p>
    </Card>
  );
}
